import { OrderFailReason, OrderStatus } from "../../../models/order";

const ORDERS_PATH = "/app/orders";
const NO_COMMENT_SIGNATURE = "(без комментария)";
/** Столько слов комментария учитывает бэк (AllOrderItemsRepository) */
const MAX_COMMENT_WORDS = 20;
/** Плейсхолдеры сигнатуры комментария (OrderCommentSignature на бэке) */
const PLACEHOLDERS = /#N\b|\bN\b|\b(?:URL|GUID|DATE|USER|ADMIN)\b/g;

/** Фильтры списка ордеров, которые передаются из аналитики через URL */
export type OrdersDrillDown = {
    failReason?: OrderFailReason;
    comment?: string;
    gameId?: number;
    from?: Date;
    to?: Date;
};

/** Ссылка на «Главную» списка ордеров с фильтрами; пустые значения пропускаются */
export function ordersListUrl(
    params: Omit<OrdersDrillDown, "failReason"> & { status?: OrderStatus; failReason?: OrderFailReason | null }
): string {
    const q = new URLSearchParams();
    if (params.status !== undefined) {
        q.set("status", String(params.status));
    }
    if (params.failReason !== undefined && params.failReason !== null) {
        q.set("failReason", String(params.failReason));
    }
    if (params.comment?.trim()) {
        q.set("comment", params.comment.trim());
    }
    if (params.gameId) {
        q.set("gameId", String(params.gameId));
    }
    if (params.from) {
        q.set("from", params.from.toISOString());
    }
    if (params.to) {
        q.set("to", params.to.toISOString());
    }
    return `${ORDERS_PATH}?${q.toString()}#general`;
}

/**
 * Слова сигнатуры для фильтра «Текст ошибки»: без плейсхолдеров и знаков, оставшихся от них, не больше 20 слов.
 * Бэк ищет комментарии, где встречается каждое слово, — так находятся и тексты с двойными пробелами и &lt;br&gt;.
 */
export function signatureSearchText(signature: string): string {
    if (signature.trim() === NO_COMMENT_SIGNATURE) {
        return "";
    }
    return signature
        .replace(PLACEHOLDERS, " ")
        .split(/\s+/)
        .filter((word) => /[\p{L}\p{N}]/u.test(word))
        .slice(0, MAX_COMMENT_WORDS)
        .join(" ");
}

function positiveInt(value: string | null): number | undefined {
    const n = Number(value);
    return value && Number.isInteger(n) && n >= 0 ? n : undefined;
}

function date(value: string | null): Date | undefined {
    const d = value ? new Date(value) : undefined;
    return d && !Number.isNaN(d.getTime()) ? d : undefined;
}

/** Статус из query: 0 (New) — тоже статус, пусто или не число — нет фильтра */
export function parseStatusParam(value: string | null): number | undefined {
    return positiveInt(value);
}

/** Фильтры перехода из query (status читается отдельно — parseStatusParam) */
export function parseOrdersDrillDown(params: URLSearchParams): OrdersDrillDown {
    const result: OrdersDrillDown = {};
    const failReason = positiveInt(params.get("failReason"));
    const gameId = positiveInt(params.get("gameId"));
    const comment = params.get("comment")?.trim();
    const from = date(params.get("from"));
    const to = date(params.get("to"));

    if (failReason !== undefined && failReason in OrderFailReason) {
        result.failReason = failReason as OrderFailReason;
    }
    if (comment) {
        result.comment = comment;
    }
    if (gameId) {
        result.gameId = gameId;
    }
    if (from) {
        result.from = from;
    }
    if (to) {
        result.to = to;
    }
    return result;
}
